import { routes } from "@/routes/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { AUDIT_SCOPE, CONTENT, DESCRIPTION, EFFECTIVE_DATE, INCLUDE_STATE_LAW, LICENSE_CATEGORY, STATE, STATUS, TEMPLATE_NAME, VERSION_NO } from "./consts";
import { useNavigate, useParams } from "react-router-dom";
import { addSchema, formValues } from "./validations";
import { toast } from "@/components/Toast";
import { useAddContractStateTemplateMutation, useGetContactStateTemplateById, useGetStates, useUpdateContactStateTemplateMutation } from "@/services";
import { capitalize, getRequiredFields } from "@/lib/utils";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";

const useAddEdit = () => {
  const {id, action} = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isView = action == "view";
  const [isAddResponse, setIsAddResponse] = useState<boolean>(false);

  const form = useForm<formValues>({
    resolver: zodResolver(addSchema),
    defaultValues: {
      [STATE]: "",
      [LICENSE_CATEGORY]: "",
      [AUDIT_SCOPE]: "",
      [EFFECTIVE_DATE]: "",
      [DESCRIPTION]: "",
      [INCLUDE_STATE_LAW]: false,
      [CONTENT]: '',
      [TEMPLATE_NAME]: '',
      [VERSION_NO]: '',
      [STATUS]: ''
    },
  });

  const requiredFields = getRequiredFields(addSchema);

  const communityDetail = useSelector(
    (state: RootState) => state.currentData.communityData
  );

  // const stateList = useSelector(
  //   (state: RootState) => state.currentData.stateData
  // );
  
  const breadcrumbData = useMemo(
    () => [
      {
        link: `/${routes.admin}/${routes.masterData}`,
        name: `Templates - ${communityDetail?.name || ""}`,
      },
      {
        link: `/${routes.admin}/${routes.masterData}/${routes.templates}/${routes.customTemplate}/${routes.stateLevelTnc}`,
        name: `State Level TnC Template`,
      },
      {
        link: "",
        name: <span className="text-primary">{id ? `${capitalize(action as any)} Template` : 'Add New Template'}</span>,
      },
    ],
    []
  )

  const {data: stateList, isLoading: isStateLoading} = useGetStates({});
  
  const onBackClick = () => {
    navigate(`/${routes.admin}/${routes.masterData}/${routes.templates}/${routes.customTemplate}/${routes.stateLevelTnc}`);
  };

  const {data: stateLawData, isLoading: isStateLawDataLoading} = useGetContactStateTemplateById({id: id as string, queryParams: {
    enabled: !!id
  }});

  useEffect(() => {
    if (stateLawData?.data) {
      form.reset({
        [STATE]: stateLawData?.data?.[STATE]?.toString() || "",
        [LICENSE_CATEGORY]: stateLawData?.data?.[LICENSE_CATEGORY] || "",
        [AUDIT_SCOPE]: stateLawData?.data?.[AUDIT_SCOPE] || "",
        [EFFECTIVE_DATE]: stateLawData?.data?.[EFFECTIVE_DATE] ? stateLawData?.data?.[EFFECTIVE_DATE]?.toString() : "",
        [DESCRIPTION]: stateLawData?.data?.[DESCRIPTION] || "",
        [INCLUDE_STATE_LAW]: stateLawData?.data?.includeStateLaws || false,
        [CONTENT]: stateLawData?.data?.[CONTENT] || "",
        [TEMPLATE_NAME]: stateLawData?.data?.[TEMPLATE_NAME] || "",
        [VERSION_NO]: stateLawData?.data?.[VERSION_NO] || ""
      });
    }
  }, [stateLawData]);

  const { mutate: addInfo, isPending: isAddPending } = useAddContractStateTemplateMutation({
    onError: (error?: any) => {
      toast.error(
        typeof error?.response?.data?.message === "string"
          ? error?.response?.data?.message
          : "Something went wrong"
      );
      return error?.response?.data?.message;
    },
    onSuccess: (data: any) => {
      toast.success("Templated Added Successfully");
      setIsAddResponse(true);
      form.setValue(CONTENT, data?.data?.data?.[CONTENT]);
      form.setValue(TEMPLATE_NAME, data?.data?.data?.[TEMPLATE_NAME]);
      form.setValue(VERSION_NO, data?.data?.data?.[VERSION_NO]);
      // queryClient.invalidateQueries({
      //   queryKey: ["contract-state-law"],
      // });
      onBackClick();
    },
  });

  const { mutate: updateInfo, isPending: isUpdatePending } = useUpdateContactStateTemplateMutation({
    onError: (error?: any) => {
      toast.error(
        typeof error?.response?.data?.message === "string"
          ? error?.response?.data?.message
          : "Something went wrong"
      );
      return error?.response?.data?.message;
    },
    onSuccess: () => {
      toast.success("Template Updated Successfully");
      queryClient.invalidateQueries({
        queryKey: ["contract-state-law"],
      });
      onBackClick();
    },
  });

  const onSubmit = (data: formValues, status: string) => {
    // const _data = removeEmptyFields(setEmptyFieldsToNull(data));

    if (id) {
      updateInfo({ stateTemplateId: id, data: {stateTemplateId: id, [CONTENT]: data[CONTENT], [STATUS]: status} });
    } else {
      delete data[CONTENT];
      delete data[TEMPLATE_NAME];
      delete data[VERSION_NO];
      delete data[STATUS];
      addInfo({ data: {...data} });
    }
  };

  return {
    breadcrumbData,
    onBackClick,
    form,
    onSubmit,
    isAddPending,
    requiredFields,
    onCancel: onBackClick,
    stateList,
    isStateLawDataLoading,
    isView,
    isUpdatePending,
    id,
    isAddResponse,
    isStateLoading,
    stateLawData
  };
};

export default useAddEdit;
